import PropTypes from 'prop-types';
// MUI
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
// Hooks
import { useSettingsContext } from 'src/components/settings';

export default function PageHeader({
  title,
  action,
  backgroundImage,
  fit = 'contain',
  bannerHeight = { xs: 200, md: 340 },
  sx,
  ...other
}) {
  const settings = useSettingsContext();

  if (backgroundImage) {
    // Title + action overlaid at the bottom, over a legibility gradient.
    const overlay = (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: { xs: 2, md: 3 },
          color: 'common.white',
          background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))',
        }}
      >
        <Typography variant="h4" sx={{ textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
          {title}
        </Typography>
        {action}
      </Stack>
    );

    return (
      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <Box
          sx={{
            mb: { xs: 3, md: 5 },
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            // 'cover' fills a fixed-height banner (cropped); 'contain' shows
            // the whole image at its natural aspect ratio.
            ...(fit === 'cover'
              ? {
                  minHeight: bannerHeight,
                  backgroundImage: `url(${backgroundImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : {}),
            ...sx,
          }}
          {...other}
        >
          {fit !== 'cover' && (
            <Box
              component="img"
              src={backgroundImage}
              alt={title}
              sx={{ display: 'block', width: 1, height: 'auto' }}
            />
          )}
          {overlay}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <Box sx={{ mb: { xs: 3, md: 5 }, ...sx }} {...other}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            {title}
          </Typography>
          {action}
        </Stack>
      </Box>
    </Container>
  );
}

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  backgroundImage: PropTypes.string,
  fit: PropTypes.oneOf(['contain', 'cover']),
  bannerHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  sx: PropTypes.object,
};